<script lang="ts">
	import classNames from 'classnames';
	import { readable } from 'svelte/store';
	import { twMerge } from 'tailwind-merge';
	import { getWishlistContext } from '#lib/features/wishlist/utils/getWishlistContext/index.js';
	import { loadWishlistDrawerContent } from '#lib/features/wishlist/utils/loadWishlistDrawerContent/loadWishlistDrawerContent.js';
	import { HeartIcon } from '$global/assets/icons/HeartIcon';
	import { Txt } from '$global/components/Txt';

	interface Props {
		dataCy: string;
		class?: string;
	}

	let { ...props }: Props = $props();

	const wishlistCtx = getWishlistContext();
	const open = wishlistCtx?.wishlistDrawer.states.open;
	// Fall back to inert stores when the context is absent (flag off), so the
	// `$store` auto-subscriptions below never run against `undefined`.
	const totalNbItems = wishlistCtx?.wishlistItems.totalNbItems ?? readable(0);
	const showWishlistIcon = wishlistCtx?.showWishlistIcon ?? readable(false);
	const animated = wishlistCtx?.wishlistItems.animated;

	// The drawer's content is loaded on demand: start fetching it as soon as the
	// visitor points at or focuses the icon (a tap fires pointerenter first).
	const prefetchDrawerContent = () => {
		loadWishlistDrawerContent().catch(() => {
			// Retried on click.
		});
	};

	// Wait for the content so the drawer never slides in empty; open anyway if
	// it failed, and the drawer retries the import itself.
	const openDrawer = async () => {
		await loadWishlistDrawerContent().catch(() => {});
		open?.set(true);
	};
</script>

{#if wishlistCtx && open && $showWishlistIcon}
	<button
		type="button"
		onclick={openDrawer}
		onpointerenter={prefetchDrawerContent}
		onfocus={prefetchDrawerContent}
		{...props}
		class={twMerge(
			'relative outline-none',
			$animated ? 'animate-icon-jump' : '',
			props.class
		)}
		data-cy={props.dataCy}
		aria-label="Open wishlist"
	>
		<HeartIcon class="h-6 w-6" thin />
		{#if $totalNbItems}
			<div
				class={classNames(
					'absolute top-[0.5rem] right-[-0.375rem] flex h-[0.875rem] items-center justify-center rounded-full bg-red-500 lg:top-[-0.125rem]',
					$totalNbItems > 99 ? 'w-[1.25rem]' : 'w-[0.875rem]'
				)}
			>
				<Txt
					class="body3 text-gray-0 text-[0.625rem] leading-[0.625rem]"
					dataCy={`${props.dataCy}-count`}
				>
					{$totalNbItems}
				</Txt>
			</div>
		{/if}
	</button>
{/if}
